import { Membership } from 'shared/src/types';
import { KeyedMutator } from 'swr';

export type InviteInfo = {
  code: string | null;
  tenantId: string | null;
  screen_hint: string | null;
  login_hint: string | null;
  shouldAcceptInvite: boolean;
};

export const INVITE_MEMBERSHIP_POLL_INTERVAL_MS = 500;
export const INVITE_MEMBERSHIP_POLL_TIMEOUT_MS = 15_000;

export const buildOrganizationReturnToAfterInvite = (tenantId: string): string =>
  `/organizations/${tenantId}`;

export const hasOrganizationMembership = (
  memberships: Membership[] | undefined,
  organizationId: string,
): boolean =>
  memberships?.some((membership) => membership.organizationId === organizationId) ?? false;

export const waitForOrganizationMembership = async (
  mutate: KeyedMutator<Membership[]>,
  organizationId: string,
  options?: {
    pollIntervalMs?: number;
    timeoutMs?: number;
  },
): Promise<boolean> => {
  const pollIntervalMs = options?.pollIntervalMs ?? INVITE_MEMBERSHIP_POLL_INTERVAL_MS;
  const timeoutMs = options?.timeoutMs ?? INVITE_MEMBERSHIP_POLL_TIMEOUT_MS;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const updatedMemberships = await mutate();

    if (hasOrganizationMembership(updatedMemberships, organizationId)) {
      return true;
    }

    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  return false;
};

/**
 * Invite links carry explicit auth context (code, tenantId, connection, login_hint).
 * Skipping silent `prompt=none` avoids an extra callback hop that can loop when no IdP session exists.
 */
export const isExplicitInviteEntryUrl = (pathname: string, search: string): boolean => {
  if (pathname !== '/invite') {
    return false;
  }

  const params = new URLSearchParams(search);
  const code = params.get('code');
  const tenantId = params.get('tenantId');

  return Boolean(code && code !== 'null' && tenantId);
};
